"use client";

import { useEffect } from "react";

/**
 * Mobile Status Bar Component
 * Sets status bar color meta tags for mobile web apps
 * This ensures the status bar matches the app's white background
 */
export function MobileStatusBar() {
  useEffect(() => {
    // Set theme-color meta tag for Android Chrome
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.setAttribute("name", "theme-color");
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.setAttribute("content", "#ffffff");

    // Set Apple mobile web app status bar style for iOS
    let appleStatusBarMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]'
    );
    if (!appleStatusBarMeta) {
      appleStatusBarMeta = document.createElement("meta");
      appleStatusBarMeta.setAttribute(
        "name",
        "apple-mobile-web-app-status-bar-style"
      );
      document.head.appendChild(appleStatusBarMeta);
    }
    appleStatusBarMeta.setAttribute("content", "default");

    // Set Apple mobile web app capable
    let appleCapableMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-capable"]'
    );
    if (!appleCapableMeta) {
      appleCapableMeta = document.createElement("meta");
      appleCapableMeta.setAttribute("name", "apple-mobile-web-app-capable");
      document.head.appendChild(appleCapableMeta);
    }
    appleCapableMeta.setAttribute("content", "yes");

    // Set format-detection to prevent Safari from auto-detecting phone numbers
    let formatDetectionMeta = document.querySelector(
      'meta[name="format-detection"]'
    );
    if (!formatDetectionMeta) {
      formatDetectionMeta = document.createElement("meta");
      formatDetectionMeta.setAttribute("name", "format-detection");
      document.head.appendChild(formatDetectionMeta);
    }
    formatDetectionMeta.setAttribute("content", "telephone=no");

    // Add meta tag to discourage Safari Reader mode
    // This helps Safari understand this is an app, not an article
    let mobileWebAppMeta = document.querySelector(
      'meta[name="mobile-web-app-capable"]'
    );
    if (!mobileWebAppMeta) {
      mobileWebAppMeta = document.createElement("meta");
      mobileWebAppMeta.setAttribute("name", "mobile-web-app-capable");
      document.head.appendChild(mobileWebAppMeta);
    }
    mobileWebAppMeta.setAttribute("content", "yes");

    // Set viewport-fit=cover to handle notch area properly
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      const content = viewportMeta.getAttribute("content") || "";
      if (!content.includes("viewport-fit=cover")) {
        viewportMeta.setAttribute(
          "content",
          `${content}, viewport-fit=cover`.replace(/^,\s*/, "")
        );
      }
    }

    // For iOS Safari, try to set status bar style via JavaScript if available
    if (typeof window !== "undefined") {
      // Check if we're in a webview that supports status bar styling
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);

      if (isIOS) {
        // Try to set status bar style for iOS
        try {
          // Some webviews support this
          if ((window as any).webkit?.messageHandlers?.setStatusBarStyle) {
            (window as any).webkit.messageHandlers.setStatusBarStyle.postMessage(
              "default"
            );
          }
        } catch (e) {
          // Silently fail if not supported
        }
      }

      if (isAndroid) {
        // For Android, try to set status bar color via JavaScript if available
        try {
          if ((window as any).Android?.setStatusBarColor) {
            (window as any).Android.setStatusBarColor("#ffffff");
          }
          // Some webviews use a different API
          if ((window as any).StatusBar) {
            (window as any).StatusBar.backgroundColorByHexString("#ffffff");
            (window as any).StatusBar.styleDefault();
          }
        } catch (e) {
          // Silently fail if not supported
        }
      }
    }
  }, []);

  return null;
}
